/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { IconName } from '@zapier/design-system';
import { Colors, Icon, Link, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../../hooks/useUniversalNavContext';
import { emitUserInteractionEvent } from '../../../../utils/avroEvents';
import { ArrowLinkIcon } from '../../ArrowLinkIcon';
import type { NavItemProduct2 } from '../../../../constants/links';

type MegaMenuProductRightProps = {
  header: NavItemProduct2['promoArea']['header'];
  submenu: NavItemProduct2['promoArea']['submenu'];
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = labelStyles('StaticProductRight', {
  root: css({
    display: 'flex',
    flexDirection: 'column',
    // necessary to prevent the right side of focus outlines from being hidden
    marginRight: 1,
    width: 300,
    // Undoes UniversalTopbarLink style, which makes all text bold when toggle is hovered/focused
    fontWeight: 'initial',
  }),
  header: css({
    display: 'flex',
    flexDirection: 'column',
    textDecoration: 'none',
  }),
  linkHeader: css({
    '&:hover [data-tag="heading"]': {
      color: Colors.zapier,
      textDecoration: 'none',
    },
    '&:hover [data-tag="subheading"]': {
      textDecoration: 'underline',
    },
    '&:hover [data-tag="arrow-icon-wrapper"]': {
      marginLeft: 4,
      opacity: 1,
    },
    '&:focus': {
      outline: `1px solid ${Colors.blueJeans}`,
    },
  }),
  heading: css({
    ...Typography.pageHeader9,
    color: Colors.earth,
    lineHeight: '24px',
    fontSize: '30px',
    fontWeight: 600,
  }),
  subheading: css({
    ...Typography.smallPrint1,
    color: Colors.neutral700,
    display: 'block',
    marginTop: 8,
  }),
  divider: css({
    margin: '16px 0',
    height: 1,
    backgroundColor: Colors.neutral300,
  }),
  linkContainer: css({
    display: 'flex',
    columnGap: 6,
    marginBottom: 18,
    textDecoration: 'none',
    color: Colors.earth,
    '&:hover [data-tag="link-heading"]': {
      color: Colors.zapier,
      textDecoration: 'none',
    },
    '&:hover [data-tag="link-icon"]': {
      color: Colors.zapier,
    },
    '&:hover [data-tag="tag-label"]': {
      textDecoration: 'none',
    },
    '&:hover [data-tag="link-description"]': {
      textDecoration: 'underline',
    },
    '&:hover [data-tag="arrow-icon-wrapper"]': {
      marginLeft: 4,
      opacity: 1,
      color: Colors.neutral700,
    },
    '&:focus': {
      outline: `1px solid ${Colors.blue}`,
    },
  }),
  linkIcon: css({
    marginTop: 5,
  }),
  linkTextContainer: css({
    display: 'flex',
    flexDirection: 'column',
  }),
  linkHeading: css({
    ...Typography.paragraph3Semibold,
    lineHeight: '30px',
    fontWeight: 500,
  }),
  linkHeadingRow: css({
    display: 'flex',
    alignItems: 'center',
    lineHeight: '30px',
    fontWeight: 500,
  }),
  descriptionWrapper: css({
    ...Typography.smallPrint3,
    lineHeight: '20px',
  }),
  linkDescription: css({
    ...Typography.smallPrint3,
    color: Colors.neutral700,
    lineHeight: '20px',
  }),
  tagLabel: (tagLabelColor: string) =>
    css({
      // These are requests from Talia,
      // even though they do not match Figma, FYI
      ...Typography.base,
      marginLeft: '6px',
      fontSize: '10px',
      lineHeight: '14px',
      fontWeight: 700,
      padding: '2px 6px',
      borderRadius: 100,
      backgroundColor: tagLabelColor,
      textDecoration: 'none',
    }),
});
const defaultTagColor = Colors.lavender;
/**
 * @private
 * Wrapper for the content inside the Product tab's right side
 */
const MegaMenuProductRight = ({
  header,
  submenu,
  onCloseMenu,
}: MegaMenuProductRightProps) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleEvent = (text: string, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: `Product tab Right: ${text}`,
    });

    if (eventType === 'click' && onCloseMenu) onCloseMenu();
  };

  const Header = () => {
    if (header.href) {
      return (
        <Link
          color={null}
          css={[Styles.header, Styles.linkHeader]}
          href={getFullyQualifiedURL(header.href)}
          target={header.target}
          onClick={() => handleEvent('Early Access Program heading', 'click')}
          onMouseEnter={() =>
            handleEvent('Early Access Program heading', 'hover')
          }
        >
          <span css={Styles.heading} data-tag="heading" data-zds>
            {header.heading}
          </span>
          {header.subheading && (
            <div data-zds>
              <span css={Styles.subheading} data-tag="subheading" data-zds>
                {header.subheading}
                <ArrowLinkIcon />
              </span>
            </div>
          )}
        </Link>
      );
    } else {
      return (
        <header css={Styles.header} data-zds>
          <span css={Styles.heading} data-tag="heading" data-zds>
            {header.heading}
          </span>
          <div data-zds>
            <span css={Styles.subheading} data-tag="subheading" data-zds>
              {header.subheading}
            </span>
          </div>
        </header>
      );
    }
  };

  return (
    <div css={Styles.root} data-zds>
      <Header />

      <hr css={Styles.divider} data-zds />

      <ul data-zds>
        {submenu.map((link) => (
          <li key={link.text} data-zds>
            <Link
              color={null}
              css={Styles.linkContainer}
              href={getFullyQualifiedURL(link.href)}
              target={link.target}
              onClick={() => handleEvent(link.text, 'click')}
              onMouseEnter={() => handleEvent(link.text, 'hover')}
            >
              <span css={Styles.linkIcon} data-tag="link-icon" data-zds>
                <Icon name={link.icon as IconName} size={18} />
              </span>
              <div css={Styles.linkTextContainer} data-zds>
                <div css={Styles.linkHeadingRow} data-zds>
                  <span
                    css={Styles.linkHeading}
                    data-tag="link-heading"
                    data-zds
                  >
                    {link.text}
                  </span>
                  {link.tag && (
                    <span
                      css={Styles.tagLabel(link.tagColor || defaultTagColor)}
                      data-tag="tag-label"
                      data-zds
                    >
                      {link.tag}
                    </span>
                  )}
                </div>
                <div css={Styles.descriptionWrapper} data-zds>
                  <span
                    css={Styles.linkDescription}
                    data-tag="link-description"
                    data-zds
                  >
                    {link.description}
                  </span>
                  <ArrowLinkIcon />
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MegaMenuProductRight;
